import type { ReactNode } from 'react';
import { Label } from '@reflex/ui/components/ui/label';
import { cn } from '@reflex/ui/lib/utils';

interface FormFieldProps {
  /** Field label. Rendered as a shadcn `<Label>`. */
  label: ReactNode;
  /** Associated input id. Connects label to input for a11y. */
  htmlFor?: string;
  /** Label id for composite controls that use `aria-labelledby`. */
  labelId?: string;
  /** Supplementary help text shown below the input. */
  hint?: ReactNode;
  /** Validation error message. Takes precedence over `hint` when set. */
  error?: ReactNode;
  /** Mark the field as required in the label (appends a ★). */
  required?: boolean;
  /** Extra class on the label. */
  labelClassName?: string;
  /** Class on the outer container. Defaults to `space-y-2`. */
  className?: string;
  children: ReactNode;
}

/**
 * Wraps the common `Label → input → error/hint` recipe used by ~25 forms
 * across the app. Prefer this over inlining `<div className="space-y-2">`.
 *
 * ```tsx
 * <FormField label="Name" htmlFor="name" hint="Displayed in lists.">
 *   <Input id="name" {...register('name')} />
 * </FormField>
 *
 * <FormField label="Email" htmlFor="email" error={errors.email?.message}>
 *   <Input id="email" type="email" {...register('email')} />
 * </FormField>
 * ```
 */
export function FormField({
  label,
  htmlFor,
  labelId,
  hint,
  error,
  required = false,
  labelClassName,
  className,
  children,
}: FormFieldProps) {
  return (
    <div className={cn('space-y-2', className)}>
      <Label id={labelId} htmlFor={htmlFor} className={labelClassName}>
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-destructive">
            *
          </span>
        )}
      </Label>
      {children}
      {error ? (
        <p className="text-xs text-destructive" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
